/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import Tbl from '@appblocks/codegeneration/Libraries/Tbl';
import TableItem from '@appblocks/codegeneration/SettingsConfigurations/TableItem';
import Cgg from '.';


export const getCodeBlocks = (feature: Cgg): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    const project: ProjectConfiguration = feature.project;
    if (!feature.getEnabled()) {
        return blocks;
    }

    const INTERFACE = 'current_interface';
    const TABLES = feature.getSettingValue('TABLES');
    const SERVER = feature.getSettingValue('SERVER');
    let SERVER_PORT = 1883;
    const settings = [];
    const CGG_MAX_SETTING_NAME_LENGTH = 0;
    const USERNAME = feature.getSettingValue('USERNAME');
    const PASSWORD = feature.getSettingValue('PASSWORD');
    const GROUPS = feature.getSettingValue('GROUPS');
    const DATA_TABLES = project.getFeature('tbl');
    const logEnabled = project.getSettingValue('log', 'ENABLED');
    const TABLES_ENABLED = DATA_TABLES && DATA_TABLES.getEnabled();
    const COMMANDSFeature = project.getFeature('commands');
    const appVersion = project.getSettingValue('general', 'FIRMWAREVERSION');
    const TLS_ENABLED = feature.getSettingValue('TLS', true) === '1';
    if (TLS_ENABLED) {
        SERVER_PORT = 8883;
    }
    let commands = [];
    if (COMMANDSFeature) {
        commands = COMMANDSFeature.getSettingValue('COMMANDS');
    }


    const variables = project.getVariables();
    let variablesCode = '';
    let variableSetCode = '';
    let tablesSendCode = '';
    let commandsCode = '';
    const enabledVariables = feature.getVariables();
    const snprintkDataTypes = [];
    const usedVariables = [];
    for (let i = 0; i < variables.length; i++) {
        const variable = variables[i];
        // only send update for variables in dashboard and settings
        const variableName = variable.getSettingValue('VARNAME');
        if (!enabledVariables.includes(variableName)) {
            continue;
        }
        let variableDatatype = variable.getSettingValue('DATATYPE');
        if (variableDatatype === TibboDataType.DATETIME) {
            variableDatatype = TibboDataType.DWORD;
        }
        if (variableDatatype === TibboDataType.TIME) {
            variableDatatype = TibboDataType.FLOAT;
        }
        const variableValue = project.convertDataType(
            variableName, variableDatatype, TibboDataType.STRING,
        );
        let variableString = `"+${variableValue}+"`;
        if (variableDatatype === TibboDataType.STRING) {
            variableString = `\\x22"+${variableValue}+"\\x22`;
        }

        let varValue = project.convertDataType(variableName, variable.getSettingValue('DATATYPE'), TibboDataType.STRING);
        if (variable.getSettingValue('DATATYPE') === TibboDataType.STRING) {
            varValue = `"\\x22"+${varValue}+"\\x22"`;
        }

        let formatDataType = '%d';
        switch (variable.getSettingValue('DATATYPE')) {
            case TibboDataType.STRING:
                formatDataType = '\\"%s\\"';
                break;
            case TibboDataType.FLOAT:
                formatDataType = '%f';
                break;
            case TibboDataType.BOOLEAN:
                formatDataType = '%d';
                break;
            case TibboDataType.INTEGER:
                formatDataType = '%d';
                break;
            default:
                formatDataType = '%d';
                break;
        }

        variablesCode += `\\"${variableName}\\":${formatDataType},`;

        blocks.push({
            location: `var_${variableName}_changed`,
            blockType: BlockType.CODE,
            template: 'cgg_variables_synced = false;',
        });
        variableSetCode += `
    if (strcmp("${variableName}", variable) == 0) {
        var_${variableName}_set(${project.convertDataType('value', TibboDataType.STRING, variableDatatype)});
    }
        `;
        usedVariables.push(variable);
    }

    if (variablesCode.endsWith(',')) {
        variablesCode = variablesCode.substring(0, variablesCode.length - 1);
    }
    variablesCode = `"{${variablesCode}}"${usedVariables.length > 0 ? ',' : ''}`;

    variablesCode += usedVariables.map((variable) => {
        const variableName = variable.getSettingValue('VARNAME');
        return variableName;
    }).join(',');

    for (let i = 0; i < commands.length; i++) {
        const command = commands[i];
        const commandName = command.getSettingValue('NAME');
        commandsCode += `
    if (strcmp("${commandName}", cmd) == 0) {
        cmd_${commandName}(data);
    }`;
    }

    if (DATA_TABLES && DATA_TABLES.getEnabled()) {
        const TABLES = DATA_TABLES.getSettingValue('TABLES');
        for (let i = 0; i < TABLES.length; i++) {
            const table = TABLES[i];
            if (table.getSettingValue('STRUCT') === 'L') {
                continue;
            }
            const tableName = TABLES[i].getSettingValue('NAME');
            tablesSendCode += `
    cgg_send_table("${tableName}")
                `;
        }
    }

    let variableGetCode = '';
    const variablesGetCode = [];
    let cmdExecCode = '';
    const widgets = feature.getSettingValue('WIDGETS');
    const widgetsArray = [];
    blocks.push({
        location: 'CGG_SETUP_TITLE',
        blockType: BlockType.LOCATION,
        template: `
        "title": "Device Console"`,
    });
    if (widgets.length > 0) {
        const variableInCode: { [variable: string]: boolean } = {};
        for (let i = 0; i < widgets.length; i++) {
            const widget = widgets[i].getWidgetObject();
            let { variable, linkType } = widget;
            try {
                const item = project.getFeature(variable);
                if (item?.id === 'variable') {
                    variable = item.getSettingValue('VARNAME');
                    const varValue = project.convertDataType(variable, item.getSettingValue('DATATYPE'), TibboDataType.STRING);
                    linkType = 'variable';
                    if (!Object.prototype.hasOwnProperty
                        .call(variableInCode, variable)) {
                        variableGetCode += `
        case "${variable}":
            var_value= ${varValue}`;
                        variableInCode[variable] = true;
                        variablesGetCode.push(`sock.setdata("\\x22${variable}\\x22:" + ${varValue})`);
                        variablesGetCode.push('sock.setdata(",")');
                    }
                } else if (item?.id === 'command') {
                    variable = item.getSettingValue('NAME');
                    linkType = 'command';
                    if (!Object.prototype.hasOwnProperty
                        .call(variableInCode, variable)) {
                        cmdExecCode += `
        case "${variable}":
            cmd_${variable}(value)`;
                        variableInCode[variable] = true;
                    }
                }
                widget.variable = variable;
                widget.linkType = linkType;
                widgetsArray.push(widget);
            } catch (ex) {
                widgets.splice(i, 1);
                i--;
            }
        }
    }

    blocks.push({
        location: 'CGG_CONSOLE_SETUP_WIDGETS',
        blockType: BlockType.CODE,
        template: `
        "updateInterval": ${feature.getSettingValue('INTERVAL') * 1000},
        "widgets": ${JSON.stringify(widgetsArray)},`,
    });

    blocks.push({
        location: 'CGG_APP_VERSION',
        blockType: BlockType.CODE,
        template: `
        "appName": "${project.name}",
        "appVersion": "${appVersion}",
        "appId": "${project.id}",
        `,
    });

    let settingsCode = '';
    for (let i = 0; i < GROUPS.length; i++) {
        const tmpBlocks = GROUPS[i].getCodeBlocks();
        for (let j = 0; j < tmpBlocks.length; j++) {
            if (tmpBlocks[j].location === 'LUIS_GROUP_ITEM') {
                settingsCode += tmpBlocks[j].template;
                if (i + 1 < GROUPS.length) {
                    settingsCode += ',';
                }
            }
        }
    }


    blocks.push({
        location: 'CGG_SETTINGS_SCHEMA',
        template: settingsCode,
    });

    blocks.push({
        location: 'PRJ_CONF',
        id: 'CONFIG_JSON_LIBRARY=y',
        template: 'CONFIG_JSON_LIBRARY=y',
    });

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
cgg_broker_hostname = ${SERVER};
cgg_broker_port = "${SERVER_PORT}";

cgg_client_id=${USERNAME};
cgg_username=${USERNAME};
cgg_password=${PASSWORD};

cgg_init(&cgg_mqtt_client_ctx);
cgg_mqtt_client_ctx.evt_cb = cgg_mqtt_client_event_handler;

cgg_client_ctx.var_callback = cgg_variable_set;
cgg_client_ctx.cmd_callback = cgg_cmd_called;
        `,
    });

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
void cgg_send_variables();
`,
    });

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
static int sub_counter = 0;
static int pub_counter = 0;
struct k_work_delayable cgg_publish_work;
struct k_work_delayable cgg_process_work;
static bool cgg_variables_synced = true;
`,
    });

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
static void cgg_work_handler()
{
    struct net_if *iface = net_if_get_default();
    while (1) {
        if (net_if_ready) 
        {
            // LOG_INF("mqtt %p", &cgg_mqtt_client_ctx);
            cgg_run(&cgg_mqtt_client_ctx);
            if (!cgg_variables_synced && sub_counter >= 6 && pub_counter >= 3) {
                cgg_send_variables();
            }
        }
        // LOG_INF("iface %p", &cgg_mqtt_client_ctx);
        k_sleep(K_MSEC(1000));
    }
    // k_work_reschedule(&cgg_process_work, K_MSEC(1000));
}

void cgg_send_variables()
{
    char json_buf[128];
                
    snprintk(json_buf, sizeof(json_buf),
    ${variablesCode}
    );
    

    cgg_mqtt_publish(
        &cgg_mqtt_client_ctx, 
        (char *)("devices/"+cgg_client_id+"/state/variables").c_str(), 
        json_buf, 
        QOS_AT_MOST_ONCE, 
        false
    );
    cgg_variables_synced = true;
}

static void cgg_mqtt_client_event_handler(struct mqtt_client *const client, const struct mqtt_evt *evt)
{
    cgg_mqtt_event_handler(client, evt);
    switch (evt->type) {
    case MQTT_EVT_CONNACK: {
        /****callback_cgg_connect_ok****/
        
        /****callback_mqtt_connect_ok****/
        sub_counter = 0;
        pub_counter = 0;
        cgg_variables_synced = false;
        cgg_subscribe(client, (char *)("devices/"+cgg_client_id+"/state/variables/+/set").c_str(), QOS_AT_MOST_ONCE);

        }
        break;

    case MQTT_EVT_DISCONNECT:
        
        break;
    case MQTT_EVT_PINGRESP:
        
        break;

    case MQTT_EVT_PUBACK:
        pub_counter++;
        if (pub_counter == 1) {
            cgg_mqtt_publish(
                &cgg_mqtt_client_ctx, 
                (char *)("devices/"+cgg_client_id+"/state/appId").c_str(), 
                (char *)("${project.id}"), 
                QOS_AT_LEAST_ONCE, 
                false
            );
        } else if (pub_counter == 2) {
            cgg_mqtt_publish(
                &cgg_mqtt_client_ctx, 
                (char *)("devices/"+cgg_client_id+"/state/appVersion").c_str(), 
                (char *)("1.0.0"), 
                QOS_AT_LEAST_ONCE, 
                false
            );
        } else if (pub_counter == 3) {

            cgg_mqtt_publish(
                &cgg_mqtt_client_ctx, 
                (char *)("devices/"+cgg_client_id+"/config").c_str(), 
                (char *)cgg_schema, 
                QOS_AT_LEAST_ONCE, 
                false
            );
        }
        break;

    case MQTT_EVT_PUBREC: {
        
        }
        break;

    case MQTT_EVT_PUBREL: {
        
        }
        break;

    case MQTT_EVT_PUBCOMP:
        
        break;

    case MQTT_EVT_SUBACK:
        sub_counter++;
        if (sub_counter == 1) {
            cgg_subscribe(client, (char *)("devices/"+cgg_client_id+"/state/tables/+/changes").c_str(), QOS_AT_MOST_ONCE);
        } else if (sub_counter == 2) {
            cgg_subscribe(client, (char *)("devices/"+cgg_client_id+"/state/tables/+/import").c_str(), QOS_AT_MOST_ONCE);
        } else if (sub_counter == 3) {
            cgg_subscribe(client, (char *)("devices/"+cgg_client_id+"/commands/#").c_str(), QOS_AT_MOST_ONCE);
        } else if (sub_counter == 4) {
            cgg_subscribe(client, (char *)("devices/"+cgg_client_id+"/firmware").c_str(), QOS_AT_MOST_ONCE);
        } else if (sub_counter == 5) {
            cgg_subscribe(client, (char *)("devices/"+cgg_client_id+"/system/#").c_str(), QOS_AT_MOST_ONCE);
        } else if (sub_counter == 6) {
            cgg_mqtt_publish(
                &cgg_mqtt_client_ctx, 
                (char *)("devices/"+cgg_client_id+"/state/appName").c_str(), 
                (char *)("${project.name}"), 
                QOS_AT_LEAST_ONCE, 
                false
            );
        }
        break;

    case MQTT_EVT_PUBLISH: {
        /****callback_mqtt_notif****/
        
        }
        break;

    default:
        break;
    }
}

void cgg_variable_set(char *variable, char *value) {
    /****cgg_variable_set****/
    ${variableSetCode}
}

void cgg_cmd_called(char *cmd, char *data) {
    /****cgg_cmd_called****/
    ${commandsCode}
}
`,
    });

    //     blocks.push({
    //         location: 'boot_complete',
    //         blockType: BlockType.CODE,
    //         template: `
    // k_work_init_delayable(&cgg_process_work, cgg_work_handler);
    // k_work_reschedule(&cgg_process_work, K_MSEC(1000));
    //         `,
    //     });

    const schemaHeaderCode = `
    {
    "appName": "${project.name}",
    "appVersion": "${appVersion}",
    "appId": "${project.id}",
    "settings":[
        ${settingsCode}
    ],
    "updateInterval": ${feature.getSettingValue('INTERVAL') * 1000},
    "widgets": ${JSON.stringify(widgetsArray)}
    }
    `;

    const lines = schemaHeaderCode.split('\n');

    const headerCode = lines.map((line) => {
        return `"${line.replace(/"/g, '\\"')}\\r\\n"`;
    }).join('\n');

    blocks.push({
        location: 'CGG_SCHEMA_HEADER',
        blockType: BlockType.CODE,
        template: `
static const unsigned char cgg_schema[] = ${headerCode};
        `,
    });

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        blockType: BlockType.CODE,
        template: `
#include <zephyr/data/json.h>
#include "cgg_client.h"
#include "cgg_schema.h"
        `,
    });

    blocks.push({
        location: 'THREAD_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
K_THREAD_DEFINE(cgg_thread, 2048, cgg_work_handler, NULL, NULL, NULL, 
    K_LOWEST_APPLICATION_THREAD_PRIO, 0, 0);
        `,
    });

    return blocks;
};
